import React from 'react';
import PropTypes from 'prop-types';
import { LuLoaderCircle, LuTriangleAlert } from 'react-icons/lu';
import Button from './Button';
import GlassIcon from './GlassIcon';
import Modal from './Modal';
import { useTranslation } from '../../i18n/LanguageContext';

const TONES = {
  warning: { gradient: 'tw-from-amber-400 tw-to-orange-500 tw-shadow-amber-500/30', button: 'warning' },
  danger: { gradient: 'tw-from-rose-400 tw-to-rose-600 tw-shadow-rose-500/30', button: 'danger' },
  primary: { gradient: 'tw-from-teal-400 tw-to-cyan-600 tw-shadow-teal-500/30', button: 'primary' },
};

/**
 * "Are you sure?" dialog. `busy` keeps it open with a spinner while the
 * confirmed action runs; closing it any other way means "no".
 */
const ConfirmDialog = ({ isOpen, title, message, confirmLabel, cancelLabel, tone = 'warning', icon: Icon = LuTriangleAlert, busy, onConfirm, onCancel }) => {
  const { t, language } = useTranslation();
  const style = TONES[tone];
  return (
    <Modal
      isOpen={isOpen}
      onClose={() => !busy && onCancel()}
      size="md"
      title={title}
      closeLabel={t('common.close')}
      lang={language}
      icon={(
        <GlassIcon gradient={style.gradient} size="xs">
          <Icon aria-hidden="true" className="tw-h-[18px] tw-w-[18px]" />
        </GlassIcon>
      )}
      footer={(
        <>
          <Button variant="ghost" onClick={onCancel} disabled={busy}>{cancelLabel}</Button>
          <Button variant={style.button} onClick={onConfirm} disabled={busy} className="max-sm:tw-flex-1">
            {busy && <LuLoaderCircle aria-hidden="true" className="tw-h-4 tw-w-4 tw-animate-spin" />}
            {confirmLabel}
          </Button>
        </>
      )}
    >
      <p className="tw-text-[15px] tw-leading-relaxed tw-text-slate-700 dark:tw-text-slate-200">{message}</p>
    </Modal>
  );
};

ConfirmDialog.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  title: PropTypes.string.isRequired,
  message: PropTypes.node.isRequired,
  confirmLabel: PropTypes.string.isRequired,
  cancelLabel: PropTypes.string.isRequired,
  tone: PropTypes.oneOf(Object.keys(TONES)),
  icon: PropTypes.elementType,
  busy: PropTypes.bool,
  onConfirm: PropTypes.func.isRequired,
  onCancel: PropTypes.func.isRequired,
};

export default ConfirmDialog;
